import { makeStyles, tokens } from '@fluentui/react-components';
import { useCallback, useState } from 'react';

export type OnOpenChangeCallback = (open: boolean) => void;

export interface UseDialogResult {
    isOpen: boolean;
    openDialog: () => void;
    closeDialog: () => void;
    onOpenChangeCallback: OnOpenChangeCallback;
}

export const useDialog = (initiallyOpen = false): UseDialogResult => {
    const [isOpen, setIsOpen] = useState(initiallyOpen);

    const openDialog = useCallback(() => {
        setIsOpen(true);
    }, [setIsOpen]);

    const closeDialog = useCallback(() => {
        setIsOpen(false);
    }, [setIsOpen]);

    const onOpenChangeCallback = useCallback(
        (open: boolean) => {
            if (!open) {
                closeDialog();
            }
        },
        [closeDialog]
    );

    return {
        isOpen,
        openDialog,
        closeDialog,
        onOpenChangeCallback,
    };
};

type UseValueDialogResult<T> = Omit<UseDialogResult, 'openDialog'> & {
    value: T | null;
    openDialog: (value: T) => void;
};

export const useValueDialog = <T>(
    initiallyOpen = false,
    initialValue?: T
): UseValueDialogResult<T> => {
    const dialogCore = useDialog(initiallyOpen);
    const { openDialog: openDialogCore, closeDialog: closeDialogCore } =
        dialogCore;

    const [value, setValue] = useState<T | null>(initialValue ?? null);

    const openDialog = useCallback<UseValueDialogResult<T>['openDialog']>(
        (newValue) => {
            setValue(newValue);
            openDialogCore();
        },
        [openDialogCore]
    );

    const closeDialog = useCallback(() => {
        setValue(null);
        closeDialogCore();
    }, [closeDialogCore]);

    return {
        ...dialogCore,
        value,
        openDialog,
        closeDialog,
    };
};

// TODO: check if this can be used in other dialogs.
export const useDialogStyles = makeStyles({
    title: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'start',
        gap: '10px',
    },
    titleIcon: {
        color: tokens.colorNeutralForegroundStaticInverted,
        background: tokens.colorBrandForeground1,
        borderRadius: tokens.borderRadiusMedium,
        padding: '6px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
});
